import { Component, OnInit, OnDestroy, Inject, ViewContainerRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { Router, NavigationEnd, NavigationStart, Route, ActivatedRoute } from '@angular/router';
import { NgSelectConfig } from '@ng-select/ng-select';
import { GlobalService } from './services/global.service';
import { Configuracao } from './models/configuracao';
import { DOCUMENT } from '@angular/common';
import { filter, map, switchMap } from 'rxjs/operators';
import { VersionCheckService } from './services/version-check.service';
import { SignalRService } from './services/signal-r.service';
import { AutenticacaoService } from './services/autenticacao.service';
import { DashboardRoutes } from './dashboard/dashboard.routing';
import { CarregandoService } from './services/carregando.service';
import { FuncionarioService } from './services/funcionario.service';
import { TelaDoFuncionario } from './models/tela-do-funcionario';
import { ConfiguracoesRoutes } from './configuracoes/configuracoes.routing';
import { NotificacaoService } from './services/notificacao.service';
import { Title } from '@angular/platform-browser';
import { AppConfigService } from './services/app-config.service';
import { HopperOctopusConfiguration } from './hopper';
import { AnalyticsService } from './services/analytics.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  private configuracaoSubscription$: Subscription;

  private notificacaoSubscription$: Subscription;

  configuracao: Configuracao;

  textoCarregando = '';

  jaAtualizou = false;

  constructor(
    private router: Router,
    private config: NgSelectConfig,
    private activatedRoute: ActivatedRoute,
    private titleService: Title,
    private globalService: GlobalService,
    private versionCheckService: VersionCheckService,
    private signalRService: SignalRService,
    private appConfigService: AppConfigService,
    private carregandoService: CarregandoService,
    private funcionarioService: FuncionarioService,
    private notificacaoService: NotificacaoService,
    private authenticationService: AutenticacaoService,
    private analyticsService: AnalyticsService,
    @Inject(DOCUMENT) private document: Document,
    @Inject(HopperOctopusConfiguration) private hopperGiConfigService: HopperOctopusConfiguration,
    public viewContainerRef: ViewContainerRef,
  ) {
    this.config.notFoundText = 'Nenhum registro encontrado...';

    router.events.pipe(filter((event) => event instanceof NavigationStart)).subscribe(async (e: NavigationStart) => {
      const currentNavigation = this.router.getCurrentNavigation();

      if (
        e.url.toLowerCase().includes('proibido') &&
        currentNavigation.extras.state &&
        currentNavigation.extras.state.permissao
      ) {
        const integrador = await this.globalService.integrador();

        if (!integrador) {
          this.redireciona(currentNavigation.extras.state.permissao);
        }
      }

      this.jaAtualizou = true;
    });

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .pipe(map(() => activatedRoute))
      .pipe(
        map((route) => {
          while (route.firstChild) route = route.firstChild;
          return route;
        }),
      )
      .pipe(switchMap((route) => route.data))
      .subscribe((event) => {
        const defaultPageTitle = this.appConfigService.getDefaultPageTitle();
        const { title } = event;

        let pageTitle = title;

        if (defaultPageTitle) {
          pageTitle = pageTitle ? `${pageTitle}: ${defaultPageTitle}` : defaultPageTitle;
        }

        if (pageTitle) {
          titleService.setTitle(pageTitle);
        }

        this.analyticsService.trackPageView(event.url, pageTitle);
      });
  }

  async ngOnInit() {
    const accessToken = this.authenticationService.obterToken();

    this.versionCheckService.iniciar();
    this.signalRService.startConnection(accessToken);
    await this.hopperGiConfigService.initializeAsync();

    // inscreve no evento de troca de configuração
    this.configuracaoSubscription$ = this.globalService.alterouConfiguracao.subscribe((conf) => {
      this.configuracao = conf;
    });

    this.notificacaoSubscription$ = this.notificacaoService.atualizacaoProgresso.subscribe((data) => {
      if (data) {
        this.textoCarregando = data.textoStatus;
      }
    });

    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
      const body = document.getElementsByTagName('body')[0];
      const modalBackdrop = document.getElementsByClassName('modal-backdrop')[0];

      if (body.classList.contains('modal-open')) {
        body.classList.remove('modal-open');
        modalBackdrop.remove();
      }
    });
  }

  ngOnDestroy() {
    this.configuracaoSubscription$.unsubscribe();
    this.notificacaoSubscription$.unsubscribe();
  }

  redireciona(permissao) {
    let link: TelaDoFuncionario;

    this.carregandoService.mostra('buscaTelas');
    this.funcionarioService.consultarTelasDoFuncionario().subscribe(
      (telas: any) => {
        const telasDoFuncionario = telas;
        this.carregandoService.esconde('buscaTelas');

        link = telasDoFuncionario.find(
          (p) =>
            p.tela !== 'loja-de-produtos' &&
            p.sistema === permissao.sistema &&
            p.pai === permissao.pai &&
            !p.tela.includes('novo') &&
            !p.tela.includes('editar') &&
            p.tela !== 'perfil',
        );

        if (!link || link === null) {
          link = telasDoFuncionario.find(
            (p) =>
              p.tela !== 'loja-de-produtos' &&
              !p.tela.includes('novo') &&
              !p.tela.includes('editar') &&
              p.tela !== 'perfil',
          );
        }

        if (link && link !== null) {
          let rota: Route;

          // sistema
          switch (link.sistema) {
            case 'gi':
              // pai
              switch (link.pai) {
                case 'configuracoes':
                  rota = ConfiguracoesRoutes[0].children.find(
                    (p) =>
                      p.data &&
                      p.data.permissao &&
                      p.data.permissao.sistema === link.sistema &&
                      p.data.permissao.tela === link.tela &&
                      p.data.permissao.pai === link.pai,
                  );
                  this.router.navigate(['/configuracoes/' + rota.path]);
                  break;

                default:
                  rota = DashboardRoutes[0].children.find(
                    (p) =>
                      p.data &&
                      p.data.permissao &&
                      p.data.permissao.sistema === link.sistema &&
                      p.data.permissao.tela === link.tela &&
                      p.data.permissao.pai === link.pai,
                  );
                  this.router.navigate(['/' + rota.path]);
                  break;
              }

              break;

            default:
              break;
          }
        }
      },
      (erro) => {
        this.carregandoService.esconde('buscaTelas');
        this.notificacaoService.erro(erro);
      },
    );
  }

  loadStyle(styleName: string) {
    const head = this.document.getElementsByTagName('head')[0];

    let themeLink = this.document.getElementById('client-theme') as HTMLLinkElement;
    if (themeLink) {
      themeLink.href = styleName;
    } else {
      const style = this.document.createElement('link');
      style.id = 'client-theme';
      style.rel = 'stylesheet';
      style.href = `${styleName}`;

      head.appendChild(style);
    }
  }
}
